import { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react'
import { sb } from '../utils/config'
import { uniqueNE, simplify, STOP_WORDS } from '../utils/helpers'
import { useAuth } from './AuthContext'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const AppContext = createContext()
export const useApp = () => useContext(AppContext)

// Profile persistence helpers
function loadProfiles() { try { const s = localStorage.getItem('cg_profiles'); return s ? JSON.parse(s) : [] } catch { return [] } }
function saveProfiles(p) { try { localStorage.setItem('cg_profiles', JSON.stringify(p)) } catch {} }
function loadActiveIdx() { try { return parseInt(localStorage.getItem('cg_active_idx') || '0', 10) } catch { return 0 } }
function saveActiveIdx(i) { try { localStorage.setItem('cg_active_idx', String(i)) } catch {} }

export function AppProvider({ children }) {
  const { user } = useAuth()
  const [page, setPageRaw] = useState(() => {
    try { return sessionStorage.getItem('cg_page') || 'onboarding' } catch { return 'onboarding' }
  })
  function setPage(p) { setPageRaw(p); try { sessionStorage.setItem('cg_page', p) } catch {} }
  const [videos, setVideos] = useState([])
  const [beats, setBeats] = useState({})
  // Explorer → Strategy bridge: when user picks a format in Explorer to script
  // Shape: { video, why } — why = user intent / search query / Merlin conversation context
  const [exploreChoice, setExploreChoice] = useState(null)

  // Merlin chat → Strategy bridge: when user clicks "Lancer une analyse" dans le chat
  // pour trouver un format qui correspond à son intent. StrategyPage détecte cette
  // valeur au mount et trigger automatiquement fetchSelections avec ce feedback.
  // Shape: { feedback: string } — feedback = description du sujet/script à matcher
  const [pendingFormatSearch, setPendingFormatSearch] = useState(null)

  // Merlin chat → Evaluate bridge: quand l'user colle un script dans le chat et
  // demande de l'améliorer/évaluer. Le planner répond avec [[EVALUATE:scriptText|label]],
  // au clic le script atterrit sur EvaluatePage pré-rempli. Évite que Merlin
  // ré-écrive en chat (cf. blocker #4 baseline) et capture le user dans le funnel.
  // Shape: { scriptText: string }
  const [pendingEvaluateScript, setPendingEvaluateScript] = useState(null)

  // Onboarding → Strategy bridge : signal "génère le 1er script auto".
  // Set par OnboardingPage.finalizeProfile à la sortie de l'analyse.
  // StrategyPage le consomme : selectContentForDay → set la 1ère selection →
  // pendingQuestionGen → auto-skip vers `generate` → review. L'user voit son
  // 1er script sans avoir à pick un format manuellement.
  // Shape: boolean (true quand on doit déclencher, reset à false dès consommé)
  const [pendingFirstScript, setPendingFirstScript] = useState(false)

  // Global Merlin chat state — shared across all pages
  const [showMerlinChat, setShowMerlinChat] = useState(false)
  const [merlinMessages, setMerlinMessages] = useState([])
  const [merlinLoading, setMerlinLoading] = useState(false)

  // Current work context — set by StrategyPage so MerlinChat can see the active script
  const [currentWork, setCurrentWork] = useState(null)  // { script, analysis, format, phase, questions, answers }

  // Signal for StrategyPage to reset to the "select" phase (formats feed).
  // Incremented whenever the user clicks the Merlin logo in the NavBar:
  // - If already on /strategy, StrategyPage watches this counter and resets
  //   its internal phase/selection state to show the formats feed fresh.
  // - Decoupled from `page` so a simple click on Merlin works whether or not
  //   you're already on the page (we always navigate AND signal).
  const [strategyResetSignal, setStrategyResetSignal] = useState(0)
  const requestStrategyReset = () => setStrategyResetSignal(n => n + 1)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Multi-profile system
  const [profiles, setProfilesRaw] = useState(() => loadProfiles())
  const [activeProfileIdx, setActiveIdxRaw] = useState(() => loadActiveIdx())
  const profile = profiles[activeProfileIdx] || null

  // Saved formats library (persisted in localStorage, PER PROFILE)
  function getFormatsKey(profileId) { return `cg_saved_formats_${profileId || 'default'}` }
  const [savedFormats, setSavedFormatsRaw] = useState([])

  // Load formats when profile changes
  useEffect(() => {
    const pid = profiles[activeProfileIdx]?.id || profiles[activeProfileIdx]?.name
    if (!pid) return
    try {
      const perProfile = localStorage.getItem(getFormatsKey(pid))
      if (perProfile) {
        setSavedFormatsRaw(JSON.parse(perProfile))
      } else if (profiles.length === 1) {
        // Migration fallback — ONLY for the very first profile (one-shot from legacy global key)
        const global = localStorage.getItem('cg_saved_formats')
        if (global) {
          const parsed = JSON.parse(global)
          setSavedFormatsRaw(parsed)
          // Persist under profile-specific key and remove global to prevent contamination
          try {
            localStorage.setItem(getFormatsKey(pid), global)
            localStorage.removeItem('cg_saved_formats')
          } catch {}
        } else {
          setSavedFormatsRaw([])
        }
      } else {
        // New or additional profile — start empty, never fall back to global
        setSavedFormatsRaw([])
      }
    } catch { setSavedFormatsRaw([]) }
  }, [activeProfileIdx, profiles.length])

  function setSavedFormats(f) {
    const val = typeof f === 'function' ? f(savedFormats) : f
    setSavedFormatsRaw(val)
    const pid = profiles[activeProfileIdx]?.id || profiles[activeProfileIdx]?.name
    try { localStorage.setItem(getFormatsKey(pid), JSON.stringify(val)) } catch {}
  }
  function addSavedFormat(format) {
    if (savedFormats.some(f => f.video?.id === format.video?.id)) return
    setSavedFormats(prev => [{ ...format, savedAt: new Date().toISOString() }, ...prev])
  }
  function removeSavedFormat(videoId) {
    setSavedFormats(prev => prev.filter(f => f.video?.id !== videoId))
  }

  // Rejected formats ("Je n'aime pas ce format") — filtered out of the feed
  // so the same clunky proposition doesn't keep reappearing. Persisted
  // per profile like savedFormats, so rejects are stable across sessions
  // but stay profile-specific (what one persona dislikes may fit another).
  function getRejectedKey(pid) { return `cg_rejected_formats_${pid || 'default'}` }
  const [rejectedFormats, setRejectedFormatsRaw] = useState([])
  useEffect(() => {
    const pid = profiles[activeProfileIdx]?.id || profiles[activeProfileIdx]?.name
    if (!pid) return
    try {
      const raw = localStorage.getItem(getRejectedKey(pid))
      setRejectedFormatsRaw(raw ? JSON.parse(raw) : [])
    } catch { setRejectedFormatsRaw([]) }
  }, [activeProfileIdx, profiles.length])
  function setRejectedFormats(f) {
    const val = typeof f === 'function' ? f(rejectedFormats) : f
    setRejectedFormatsRaw(val)
    const pid = profiles[activeProfileIdx]?.id || profiles[activeProfileIdx]?.name
    try { localStorage.setItem(getRejectedKey(pid), JSON.stringify(val)) } catch {}
  }
  function addRejectedFormat(videoId, meta = {}) {
    if (!videoId) return
    if (rejectedFormats.some(f => f.videoId === videoId)) return
    setRejectedFormats(prev => [{ videoId, rejectedAt: new Date().toISOString(), ...meta }, ...prev])
  }
  function removeRejectedFormat(videoId) {
    setRejectedFormats(prev => prev.filter(f => f.videoId !== videoId))
  }

  function setProfiles(p) { const arr = typeof p === 'function' ? p(profiles) : p; setProfilesRaw(arr); saveProfiles(arr) }
  function setActiveIdx(i) { setActiveIdxRaw(i); saveActiveIdx(i) }

  // Clear cached audience data when a profile changes (signals, portrait, context)
  function clearProfileCache(profileId) {
    if (!profileId) return
    try {
      localStorage.removeItem(`cg_audience_signals_${profileId}`)
      localStorage.removeItem(`cg_audience_portrait_${profileId}`)
      localStorage.removeItem(`cg_user_context_${profileId}`)
      localStorage.removeItem(`merlin_bio_checklist_${profileId}`)
    } catch {}
  }

  function setProfile(p) {
    if (!p) return
    if (!p.id) p.id = 'p_' + Date.now()
    if (!p.name) p.name = p.description ? p.description.substring(0, 30) : 'Profil'
    if (profiles.length === 0) {
      setProfiles([p])
      setActiveIdx(0)
    } else {
      const old = profiles[activeProfileIdx]
      const updated = [...profiles]
      updated[activeProfileIdx] = { ...updated[activeProfileIdx], ...p }
      setProfiles(updated)
      // If description/audience/niche changed, clear stale audience cache
      if (old && (p.description !== old.description || p.audience !== old.audience || p.niche !== old.niche)) {
        clearProfileCache(old.id || old.name)
      }
    }
  }

  function addProfile(p) {
    if (!p.id) p.id = 'p_' + Date.now()
    if (!p.name) p.name = p.description ? p.description.substring(0, 30) : 'Profil'
    const newProfiles = [...profiles, p]
    setProfiles(newProfiles)
    setActiveIdx(newProfiles.length - 1)
  }

  function switchProfile(idx) {
    if (idx >= 0 && idx < profiles.length) {
      setActiveIdx(idx)
      setPage('strategy')
    }
  }

  function updateProfile(idx, updates) {
    const old = profiles[idx]
    const updated = [...profiles]
    updated[idx] = { ...updated[idx], ...updates }
    setProfiles(updated)
    // If description/audience/niche changed, clear stale audience cache
    if (old && (updates.description || updates.audience || updates.niche)) {
      clearProfileCache(old.id || old.name)
    }
  }

  function deleteProfile(idx) {
    if (profiles.length <= 1) return
    const updated = profiles.filter((_, i) => i !== idx)
    setProfiles(updated)
    if (activeProfileIdx >= updated.length) setActiveIdx(updated.length - 1)
    else if (activeProfileIdx > idx) setActiveIdx(activeProfileIdx - 1)
  }

  // Auto-restore profiles from Supabase when localStorage is empty
  // (happens after: new browser/device, cleared cache, ou navigation privée).
  // Sans ce restore, les users connectés avec un profil DB sont renvoyés à
  // tort sur l'onboarding (vu en prod : navigation privée → onboarding alors
  // que profil DB existe).
  const _profilesRestoreAttempted = useRef(false)
  // ★ profilesRestoring : true tant qu'on n'a PAS encore tenté le restore.
  // L'App.jsx s'en sert pour afficher un loader au lieu d'OnboardingPage
  // pendant la fenêtre de course initiale (user authenticated mais profiles
  // pas encore restaurés). Sans ça, l'user voit OnboardingPage flasher avant
  // que le profil DB soit restauré, et croit qu'il doit refaire l'onboarding.
  const [profilesRestoring, setProfilesRestoring] = useState(false)
  const [profilesRestoreDone, setProfilesRestoreDone] = useState(false)
  useEffect(() => {
    if (!user?.id) { _profilesRestoreAttempted.current = false; setProfilesRestoreDone(false); return }
    if (profiles.length > 0) { setProfilesRestoreDone(true); return } // local cache wins
    if (_profilesRestoreAttempted.current) return // only try once per session
    _profilesRestoreAttempted.current = true
    setProfilesRestoring(true)

    ;(async () => {
      try {
        const { data, error } = await sb.from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .order('position', { ascending: true })
        if (error) { console.warn('[AppContext] Profile restore error:', error.message); setProfilesRestoring(false); setProfilesRestoreDone(true); return }
        if (!data || data.length === 0) { setProfilesRestoring(false); setProfilesRestoreDone(true); return } // genuinely new user → onboarding is correct
        // Rebuild local-shape profiles from DB rows (keep DB UUIDs as ids)
        const restored = data.map(row => ({
          id: row.id,
          user_id: row.user_id, // ★ critical : sans ça, semanticSearch.js + autres ne peuvent pas tagger les pipeline_logs avec user_id
          userId: row.user_id,
          name: row.name,
          description: row.description || '',
          audience: row.audience || '',
          objective: row.objective || '',
          has_product: !!row.has_product,
          hasProduct: !!row.has_product,
          product_description: row.product_description || '',
          productDescription: row.product_description || '',
          production_style: row.production_style || '',
          productionStyle: row.production_style || '',
          posts_per_week: row.posts_per_week || null,
          postsPerWeek: row.posts_per_week || null,
          emotionalTone: row.emotional_tone || '',
          // Persona client (from migration 010)
          creatorPain: row.creator_pain || '',
          merlinExpectation: row.merlin_expectation || '',
          // Audience deep fields (from migration 010)
          audiencePain: row.audience_pain || '',
          audienceDesire: row.audience_desire || '',
          audienceEnemy: row.audience_enemy || '',
          creatorMethod: row.creator_method || '',
          audienceTarget: row.audience_target || '',
        }))
        console.log(`[AppContext] Restored ${restored.length} profile(s) from Supabase (localStorage was empty)`)
        setProfiles(restored)
        setActiveIdx(0)
      } catch (e) {
        console.warn('[AppContext] Profile restore failed:', e.message)
      } finally {
        setProfilesRestoring(false)
        setProfilesRestoreDone(true)
      }
    })()
  }, [user?.id, profiles.length])

  // Auto-sync local profiles (p_xxx IDs) to Supabase to get real UUIDs
  useEffect(() => {
    if (!user?.id || profiles.length === 0) return
    const localProfiles = profiles.filter(p => p.id && !UUID_RE.test(p.id))
    if (localProfiles.length === 0) return

    ;(async () => {
      let changed = false
      const updated = [...profiles]
      for (let i = 0; i < updated.length; i++) {
        const p = updated[i]
        if (!p.id || UUID_RE.test(p.id)) continue // already has UUID
        try {
          const { data, error: err } = await sb.from('profiles').insert({
            user_id: user.id,
            name: (p.name || 'Profil').substring(0, 30),
            description: p.description || null,
            audience: p.audience || null,
            objective: p.objective || null,
            has_product: p.hasProduct ?? p.has_product ?? false,
            product_description: p.productDescription || p.product_description || null,
            production_style: p.productionStyle || p.production_style || null,
            posts_per_week: p.postsPerWeek || p.posts_per_week || null,
            emotional_tone: p.emotionalTone || null,
            // Persona client (new — from migration 010)
            creator_pain: p.creatorPain || null,
            merlin_expectation: p.merlinExpectation || null,
            // Audience deep fields (were captured in onboarding but never persisted)
            audience_pain: p.audiencePain || null,
            audience_desire: p.audienceDesire || null,
            audience_enemy: p.audienceEnemy || null,
            creator_method: p.creatorMethod || null,
            audience_target: p.audienceTarget || null,
            position: i,
          }).select().single()
          if (err) { console.warn('[AppContext] Profile sync error:', err.message); continue }
          console.log(`[AppContext] Synced local profile "${p.name}" → UUID ${data.id}`)
          // Migrate user_instagram_profiles and user_videos with null profile_id to new UUID
          await sb.from('user_instagram_profiles').update({ profile_id: data.id }).eq('user_id', user.id).is('profile_id', null)
          await sb.from('user_videos').update({ profile_id: data.id }).eq('user_id', user.id).is('profile_id', null)
          await sb.from('user_content_insights').update({ profile_id: data.id }).eq('user_id', user.id).is('profile_id', null)
          updated[i] = { ...p, id: data.id }
          changed = true
        } catch (e) { console.warn('[AppContext] Profile sync failed:', e.message) }
      }
      if (changed) {
        setProfiles(updated)
      }
    })()
  }, [user?.id, profiles.length])

  // Fetch videos & beats (with sessionStorage cache for instant restore on tab switch)
  useEffect(() => {
    (async () => {
      // 1. Instant restore from cache (avoids blank screen on tab switch / Chrome suspend)
      try {
        const cachedVideos = sessionStorage.getItem('cg_videos_cache')
        const cachedBeats = sessionStorage.getItem('cg_beats_cache_v2')
        if (cachedVideos) setVideos(JSON.parse(cachedVideos))
        if (cachedBeats) setBeats(JSON.parse(cachedBeats))
        if (cachedVideos) setLoading(false) // show cached data immediately
      } catch {}

      // 2. Fresh fetch from Supabase (updates in background)
      try {
        const { data, error: e } = await sb.from('videos')
          .select('id,airtable_id,titre,lien,createur_nom,niche_nom,niche_categorie,hook_audio_template,hook_visuel_template,hook_audio_verbatim,hook_visuel_verbatim,hook_audio_template_fr,hook_visuel_template_fr,formule_syntaxique,format_nom,categorie_format,arc_narratif,type_structure,raison_partager,raison_enregistrer,tribu_identitaire,tribu_aspirationnelle,ennemi_visible,ennemi_systemique,ennemi_interieur,douleur_quotidienne,peur_profonde,honte_secrete,desir_tangible,desir_identitaire,emotion_dominante,vehicule_solution,levier_autorite,preuve_sociale,urgence_rarete,curiosite_gap,nostalgie,niveau_conscience,mecanisme_viral,evergreen_tendance,universalite,emotion_primaire,biais_cognitifs,similar_to_id,similarity_score,vues,likes,commentaires_count,partages,enregistrements,taux_engagement,ratio_partages,ratio_saves,viralite,video_mp4_url,script_complet,script_fr,description,resume,cta,position_funnel,resume_thematique,date_publication,plateforme,langue,duree_secondes,accroche_visuelle,interruption_pattern,type_interruption,force_gap,nombre_actes,boucle_ouverte,twist,point_payoff,capital_social,potentiel_debat,intention_strategique,action_engagement,niveau_montage,voix,forces_format,regles_format,mecanisme_rhetorique,longueur_hook,intensite_emotionnelle,emotions_secondaires,hook_variables,sentiment_commentaires,verbatims_cles_commentaires,commentaires_filtres')
          .order('created_at', { ascending: false })
        if (e) throw e
        setVideos(data || [])
        try { sessionStorage.setItem('cg_videos_cache', JSON.stringify(data || [])) } catch {}

        const { data: bData } = await sb.from('structure_narrative')
          .select('video_id,beat,fonction,seau_active,technique,variable_cle,valeur_variable,texte_cle,description_visuelle,visual_data,image_cle_url')
          .order('beat', { ascending: true })
        if (bData) {
          const map = {}
          bData.forEach(b => { if (!map[b.video_id]) map[b.video_id] = []; map[b.video_id].push(b) })
          setBeats(map)
          try { sessionStorage.setItem('cg_beats_cache_v2', JSON.stringify(map)) } catch {}
        }
      } catch (e) {
        console.error(e)
        setError(e.message)
      }
      setLoading(false)
    })()
  }, [])

  const niches = useMemo(() => uniqueNE(videos.map(v => v.niche_categorie)), [videos])
  const arcs = useMemo(() => uniqueNE(videos.map(v => v.arc_narratif)), [videos])

  // getNicheInsights  -  match UNIQUEMENT sur niche_nom/niche_categorie
  function getNicheInsights(description) {
    if (!description || description.length < 3) return null
    const words = description.toLowerCase().split(/\s+/).filter(w => w.length > 2)
    const filteredWords = words.filter(w => !STOP_WORDS.includes(w) && w.length > 3)
    if (filteredWords.length === 0) return { matched: 0, peurs: [], douleurs: [], desirs: [], ennemis: [], tribus: [], vehicules: [] }

    const matched = videos.filter(v => {
      const nicheFields = [v.niche_nom, v.niche_categorie].filter(Boolean).join(' ').toLowerCase()
      return filteredWords.some(w => nicheFields.includes(w))
    })
    if (matched.length === 0) return { matched: 0, peurs: [], douleurs: [], desirs: [], ennemis: [], tribus: [], vehicules: [] }

    const collect = (key) => {
      const counts = {}, weights = {}
      matched.forEach(v => {
        if (!v[key]) return
        const w = Math.log10(Math.max(v.vues || 1, 1))
        counts[v[key]] = (counts[v[key]] || 0) + 1
        weights[v[key]] = (weights[v[key]] || 0) + w
      })
      return Object.entries(weights).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([val, w]) => ({
        val, ct: counts[val], pct: Math.round(counts[val] / matched.length * 100), weight: Math.round(w * 10) / 10,
      }))
    }

    return {
      matched: matched.length,
      matchRatio: matched.length / Math.max(videos.length, 1),
      peurs: collect('peur_profonde'),
      douleurs: collect('douleur_quotidienne'),
      desirs: collect('desir_tangible'),
      desirs_id: collect('desir_identitaire'),
      ennemis: collect('ennemi_visible'),
      ennemis_sys: collect('ennemi_systemique'),
      ennemis_int: collect('ennemi_interieur'),
      tribus: collect('tribu_identitaire'),
      vehicules: collect('vehicule_solution'),
      hontes: collect('honte_secrete'),
      emotions: collect('emotion_dominante'),
      mecanismes: collect('mecanisme_viral'),
      arcs: collect('arc_narratif'),
    }
  }

  const beatsKnowledge = useMemo(() => {
    const allBeats = Object.values(beats).flat()
    if (allBeats.length === 0) return null
    const byFonction = {}
    allBeats.forEach(b => {
      if (!b.fonction) return
      if (!byFonction[b.fonction]) byFonction[b.fonction] = { count: 0, techniques: {}, seaux: {}, textes: [] }
      const f = byFonction[b.fonction]
      f.count++
      if (b.technique) f.techniques[b.technique] = (f.techniques[b.technique] || 0) + 1
      if (b.seau_active) f.seaux[b.seau_active] = (f.seaux[b.seau_active] || 0) + 1
      if (b.texte_cle && b.texte_cle.length > 10) f.textes.push(b.texte_cle)
    })
    const knowledge = {}
    for (const [fn, data] of Object.entries(byFonction)) {
      knowledge[fn] = {
        count: data.count,
        techniques: Object.entries(data.techniques).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([t, c]) => ({ t, c, pct: Math.round(c / data.count * 100) })),
        seaux: Object.entries(data.seaux).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([s, c]) => ({ s, c, pct: Math.round(c / data.count * 100) })),
        textes: data.textes.slice(0, 8),
      }
    }
    const totalBeats = allBeats.length
    const videosWithBeats = Object.keys(beats).length
    const avgBeatsPerVideo = Math.round(totalBeats / Math.max(videosWithBeats, 1) * 10) / 10
    const techniqueGlobal = {}
    allBeats.forEach(b => { if (b.technique) techniqueGlobal[b.technique] = (techniqueGlobal[b.technique] || 0) + 1 })
    return {
      byFonction: knowledge, totalBeats, videosWithBeats, avgBeatsPerVideo,
      techniqueGlobal: Object.entries(techniqueGlobal).sort((a, b) => b[1] - a[1]).map(([t, c]) => ({ t, c })),
    }
  }, [beats])

  const beatsKnowledgeSummary = useMemo(() => {
    if (!beatsKnowledge) return ''
    const lines = [`BASE (${beatsKnowledge.totalBeats} etapes, ${beatsKnowledge.videosWithBeats} videos, moy. ${beatsKnowledge.avgBeatsPerVideo}/video)`]
    for (const [fn, data] of Object.entries(beatsKnowledge.byFonction)) {
      const techs = data.techniques.slice(0, 3).map(t => `${t.t}(${t.pct}%)`).join(', ')
      lines.push(`${fn}(${data.count}x): [${techs}]`)
    }
    return lines.join('\n')
  }, [beatsKnowledge])

  function deduceAudience(description, insights) {
    if (!insights || insights.matched === 0) return null
    const parts = []
    if (insights.tribus?.[0]) parts.push('des ' + simplify(insights.tribus[0].val))
    if (insights.douleurs?.[0]) parts.push('qui galerent avec ' + simplify(insights.douleurs[0].val).toLowerCase())
    if (insights.desirs?.[0]) parts.push('et qui veulent ' + simplify(insights.desirs[0].val).toLowerCase())
    return parts.length > 0 ? parts.join(' ') : null
  }

  return (
    <AppContext.Provider value={{
      page, setPage, videos, beats, beatsKnowledge, beatsKnowledgeSummary, loading, error,
      profile, setProfile, profiles, activeProfileIdx, addProfile, switchProfile, updateProfile, deleteProfile,
      profilesRestoring, profilesRestoreDone,
      niches, arcs, getNicheInsights, deduceAudience,
      exploreChoice, setExploreChoice,
      pendingFormatSearch, setPendingFormatSearch,
      pendingEvaluateScript, setPendingEvaluateScript,
      pendingFirstScript, setPendingFirstScript,
      savedFormats, addSavedFormat, removeSavedFormat,
      rejectedFormats, addRejectedFormat, removeRejectedFormat,
      showMerlinChat, setShowMerlinChat, merlinMessages, setMerlinMessages, merlinLoading, setMerlinLoading,
      currentWork, setCurrentWork,
      strategyResetSignal, requestStrategyReset,
    }}>
      {children}
    </AppContext.Provider>
  )
}
